import PageLayout from "../../components/PageLayout";
import { ReactNode } from 'react';
import { useResponsive } from "../../lib/useResponsive";

interface AboutPageProps {
  title: ReactNode;
}

const features = [
  { icon: "🤝", title: "Built on trust & relationships", desc: "Invite-only, grown through trusted community connections — friends, families, and proven referrals." },
  { icon: "💡", title: "Zero management fees — by design", desc: "Every dollar of operational cost flows back to community — events, gifts, gatherings, and member support." },
  { icon: "📈", title: "Real money in real markets", desc: "Actively managed in a Charles Schwab account. You own units backed by real portfolio value." },
  { icon: "⭐", title: "Contribution earns advantage", desc: "Rewards, discounts, and leadership roles earned through referrals, participation, and commitment." },
];

export default function AboutPage() {
  const { isMobile } = useResponsive();

  return (
    <PageLayout
      eyebrow="About SSN"
      title={<>Not just a fund.<br />A <em>community</em> built to achieve complete <em>Freedom</em>.</>}
      subtitle=""
    >
      <div style={S.prose}>
        <p style={S.aboutBox}>True freedom starts with financial security — and that is where SSN begins.</p>
        <p>
          Once your finances are secure, the rest opens up — travel the world, pursue your passion, 
          support your community, give back, or simply live life on your own terms. <strong>SSN is 
          the foundation that makes all of it possible.</strong> Every rupee and dollar invested here 
          is a step toward the life you actually want to live.
        </p>
      </div>

      <div style={S.listStack}>
        {features.map((f, i) => (
          <div key={i} style={S.listItem}>
            <div style={S.listIcon}>{f.icon}</div>
            <div>
              <strong style={S.listTitle}>{f.title}</strong>
              <p style={S.listDesc}>{f.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...S.calloutCard, padding: isMobile ? "20px 16px" : 28 }}>
        <h3 style={S.cardH3}>Why We Exist</h3>
        <p style={S.cardText}>
          Most people invest alone — without professional-grade management or community accountability. 
          SSN was built to change that. When trusted people pool discipline and capital together, 
          everyone grows further than they would alone.
        </p>
        <div style={S.motto}>"Collective discipline. Individual growth. Financial freedom for all."</div>
      </div>
    </PageLayout>
  );
}

const S: Record<string, React.CSSProperties> = {
  prose: { fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.9, display: "flex", flexDirection: "column", gap: 16, marginBottom: 32 },
  aboutBox: { fontSize: 18, color: "var(--text-primary)", fontWeight: 500, lineHeight: 1.6 },
  listStack: { display: "flex", flexDirection: "column", gap: 20, marginBottom: 40 },
  listItem: { display: "flex", gap: 16, alignItems: "flex-start" },
  listIcon: { fontSize: 24, minWidth: 32, textAlign: "center", flexShrink: 0 },
  listTitle: { display: "block", fontSize: 16, color: "var(--text-primary)", marginBottom: 4 },
  listDesc: { fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6 },
  calloutCard: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 28, marginBottom: 40 },
  cardH3: { fontSize: 18, fontWeight: 600, color: "var(--text-primary)", marginBottom: 12 },
  cardText: { fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.7, marginBottom: 16 },
  motto: { fontSize: 15, fontStyle: "italic", color: "var(--teal-light)", borderLeft: "2px solid var(--teal-light)", paddingLeft: 12, margin: "20px 0 4px 0" },
};
